// @flow
import React from 'react'
import Dropzone from 'react-dropzone'
import request from 'superagent'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import Snackbar from '@material-ui/core/Snackbar'
import TextField from '@material-ui/core/TextField'
import Grid from '@material-ui/core/Grid'
import buffer from '@turf/buffer'
import OlGeoJSON from 'ol/format/GeoJSON'
import OlFeature from 'ol/Feature'
import * as OlPolygon from 'ol/geom/Polygon'
import OlVectorLayer from 'ol/layer/Vector'
import OlVectorSource from 'ol/source/Vector'
import { olUtils, styles } from 'catena/er2_ui'
import { fetchKnownBoundaries, fetchKnownBoundary } from '../utils'

export type FilterType = {
    filter: string,
    // If the user must provide a value
    required: boolean,
    value: any,
}

export type AOIstate = {
    aoiFeature: string,
    userGeojson: {},
    method: string,
    knownBoundaryType: string,
    knownBoundaryId: string,
}

type Props = {
    classes: {
        formControl: string,
        textField: string,
    },
    er2Map: any,
    // Additional string entries that add to search
    extraFilters?: FilterType[],
    includeKnownBoundaries?: boolean,
    initialState?: {
        aoiFeature: {},
        knownBoundaryType: string,
        knownBoundaryId: string,
        method: string,
    },
    // pair of known boundary type and display string
    knownBoundaryOptions?: [string, string][],
    olstyle?: {},
    onCancel?: Function,
    onSelect: Function,
    shapes?: string[],
    theme?: styles,
    toolLabel?: string,
}

type State = {
    bufferRadius: number,
    bufferUnits: number,
    drawnFeature: {},
    drawPromptRendered: boolean,
    fetchError: boolean,
    fetchErrorRendered: boolean,
    fetchingGeometry: boolean,
    fetchingKnownBoundaries: boolean,
    helpActive: {
        boundaries: boolean,
    },
    isDrawing: boolean,
    knownBoundaries: { oid: number, name: string}[],
    knownBoundaryType: string,
    knownBoundaryId: string,
    method: string,
    // List of imported features
    userGeojson?: {},
}

function getDrawPromptMessage(msgType) {
    switch (msgType) {
    case 'DRAW_POINT':
        return 'Click to draw a point on the map'
    case 'DRAW_LINE':
        return 'Click to begin drawing a line on the map'
    case 'DRAW_POLYGON':
        return 'Click to begin drawing a polygon on the map'
    case 'DRAW_RECTANGLE':
        return 'Click to begin drawing a rectangle on the map'
    default:
        return 'Click to draw a point on the map'
    }
}

class AOISelect extends React.Component<Props, State> {
    static defaultProps = {
        extraFilters: [],
        includeKnownBoundaries: true,
        initialState: {
            knownBoundaryType: '',
            knownBoundaryId: '',
            method: '',
        },
        knownBoundaryOptions: [
            ['states', 'States'],
            ['counties', 'Counties'],
            ['cities', 'Cities'],
            ['huc8', 'HUC 8-digit Watersheds'],
            ['huc10', 'HUC 10-digit Watersheds'],
            ['huc12', 'HUC 12-digit Watersheds'],
        ],
        olstyle: {},
        onCancel: false,
        shapes: ['POINT', 'LINE', 'POLYGON', 'RECTANGLE', 'CIRCLE'],
        theme: styles,
        toolLabel: 'Select Area of Interest',
    }

    constructor(props) {
        super(props)
        this.state = {
            bufferRadius: 1,
            bufferUnits: 'miles',
            drawPromptRendered: true,
            feature: OlFeature,
            fetchError: false,
            fetchErrorRendered: true,
            fetchingGeometry: false,
            fetchingKnownBoundaries: false,
            helpActive: {
                boundaries: false,
            },
            isDrawing: false,
            knownBoundaries: [],
            knownBoundaryType: '',
            knownBoundaryId: '',
            method: '',
        }

        props.extraFilters.forEach((f) => {
            this.state[f.filter] = ''
        })
    }

    componentDidMount() {
        this.vectorSource = new OlVectorSource({
            projection: 'EPSG:3857',
        })
        this.vectorLayer = new OlVectorLayer({
            name: 'AOIselect',
            source: this.vectorSource,
            style: (feat, res) => this.getStyle(feat, res),
            zIndex: 1,
        })

        this.props.er2Map.olmap.addLayer(this.vectorLayer)
        this.props.er2Map.olmap.render()
    }

    componentWillReceiveProps(props: Props, state: State) {
        // Check if the selections should be updated
        if (!this.state.method && !this.state.fetchingKnownBoundaries && props.initialState.method) {
            const newState = { method: props.initialState.method, drawnFeature: props.initialState.aoiFeature }
            props.extraFilters.forEach((f) => {
                if (f.value && this.state[f.filter] !== f.value) {
                    newState[f.filter] = f.value
                }
            })
            this.setState(newState)
            if (props.initialState.method === 'KNOWN_BOUNDARY') {
                // Run asynchronously because fetchKnownBoundaries will call setState()
                setTimeout(() => {
                    this.fetchKnownBoundaries(props.initialState.knownBoundaryType, () => {
                        this.fetchKnownBoundary(props.initialState.knownBoundaryId)
                    })
                })
            }
        }
    }

    componentWillUnmount() {
        if (this.drawInteraction) {
            this.props.er2Map.olmap.removeInteraction(this.drawInteraction)
        }
        this.props.er2Map.olmap.removeLayer(this.vectorLayer)
    }

    activateDrawTool(method) {
        const drawToolSelected = method
        if (this.drawInteraction) {
            this.props.er2Map.olmap.removeInteraction(this.drawInteraction)
        }
        if (drawToolSelected === 'DRAW_POINT') {
            this.drawInteraction = olUtils.makeDrawPointInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt, this.state.bufferRadius, this.state.bufferUnits))
            this.props.er2Map.olmap.addInteraction(this.drawInteraction)
        } else if (drawToolSelected === 'DRAW_LINE') {
            this.drawInteraction = olUtils.makeDrawLineInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt, this.state.bufferRadius, this.state.bufferUnits))
            this.props.er2Map.olmap.addInteraction(this.drawInteraction)
        } else if (drawToolSelected === 'DRAW_POLYGON') {
            this.drawInteraction = olUtils.makeDrawPolygonInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt))
            this.props.er2Map.olmap.addInteraction(this.drawInteraction)
        } else if (drawToolSelected === 'DRAW_RECTANGLE') {
            this.drawInteraction = olUtils.makeDrawRectangleInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt))
            this.props.er2Map.olmap.addInteraction(this.drawInteraction)
        } else if (drawToolSelected === 'DRAW_CIRCLE') {
            this.drawInteraction = olUtils.makeDrawCircleInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt))
            this.props.er2Map.olmap.addInteraction(this.drawInteraction)
        }
        this.setState({ isDrawing: this.drawInteraction !== undefined })
    }

    getStyle() {
        return olUtils.getOlStyles(this.props.olstyle)
    }

    fetchKnownBoundaries(knownBoundaryType, onFetchCompleted) {
        const extent = this.props.er2Map.olmap.getView().calculateExtent(this.props.er2Map.olmap.getSize())
        const feat = new OlFeature({
            geometry: OlPolygon.fromExtent(extent),
        })

        this.setState({ fetchingKnownBoundaries: true, knownBoundaryType: knownBoundaryType || this.state.knownBoundaryType, extent })
        fetchKnownBoundaries(olUtils.writeFeatures(feat), knownBoundaryType || this.state.knownBoundaryType, (items) => {
            // Check if a boundary has already been selected
            this.setState({ fetchingKnownBoundaries: false, knownBoundaries: items.sort() })
            // Return the boundaries because setState runs asynchronously.
            if (onFetchCompleted) onFetchCompleted(items)
        }, (error) => {
            console.error(error)
            this.setState({ fetchingKnownBoundaries: false, knownBoundaries: [] })
        })
    }

    fetchKnownBoundary(boundary) {
        this.setState({ fetchingKnownBoundary: true })
        this.vectorSource.clear()
        fetchKnownBoundary(boundary, this.state.knownBoundaryType, (feat) => {
            const olFeat = olUtils.readFeatures(feat)
            this.vectorSource.addFeatures(olFeat)
            this.setState({ fetchingKnownBoundary: false, drawnFeature: feat, knownBoundaryId: boundary })
            const extent = this.vectorSource.getExtent()
            this.props.er2Map.olmap.getView().fit(extent, this.props.er2Map.olmap.getSize())
            this.onSelect()
        }, (error) => {
            console.error(error)
            this.setState({ fetchingKnownBoundary: false, drawnFeature: null })
        })
    }

    onDrawShape(evt, bufferRadius, bufferUnits) {
        this.vectorSource.clear()
        let feat = evt.feature
        if (bufferRadius) {
            // Replace geometry with buffered value
            const geojson = new OlGeoJSON()
            const featGeo = geojson.writeFeatureObject(feat, {
                featureProjection: 'EPSG:3857',
                dataProjection: 'EPSG:4326',
            })
            const buffered = buffer(featGeo, bufferRadius, { units: bufferUnits })
            feat = geojson.readFeatureFromObject(buffered, {
                featureProjection: 'EPSG:3857',
                dataProjection: 'EPSG:4326',
            })
        }
        this.vectorSource.addFeature(feat)
        this.setState({ drawnFeature: olUtils.writeFeatures(feat), isDrawing: false })
        this.props.er2Map.olmap.removeInteraction(this.drawInteraction)
        this.drawInteraction = undefined

        this.onSelect()
    }

    onDrawFeatures(featureEnvelope, userGeojson) {
        this.vectorSource.clear()
        this.vectorSource.addFeature(featureEnvelope)
        this.setState({ drawnFeature: olUtils.writeFeatures(featureEnvelope), userGeojson })
        this.props.er2Map.olmap.removeInteraction(this.drawInteraction)
        this.drawInteraction = undefined

        this.onSelect()
    }

    onSelect = () => {
        // Compose state object that represents the current state.
        const aoiState = {
            aoiFeature: this.state.drawnFeature,
            userGeojson: this.state.userGeojson,
            method: this.state.method,
            knownBoundaryType: this.state.knownBoundaryType,
            knownBoundaryId: this.state.knownBoundaryId,
        }
        this.props.onSelect(aoiState)
    }

    onReloadBoundaries = () => {
        this.fetchKnownBoundaries(this.state.knownBoundaryType)
    }

    setMethod = (evt) => {
        const method = evt.target.value
        this.vectorSource.clear()
        this.setState({ method })
        this.activateDrawTool(method)
    }

    setBoundaryType = (evt) => {
        const knownBoundaryType = evt.target.value
        this.setState({ knownBoundaryType })
        this.fetchKnownBoundaries(knownBoundaryType)
    }

    updateFilter(filter, value) {
        this.setState({ [filter]: value })
    }

    renderNotifications() {
        const drawMessage = this.state.method ? getDrawPromptMessage(this.state.method) : ''
        return (<div>
            <Snackbar
                open={this.state.drawPromptRendered === false}
                autoHideDuration={6000}
                onClose={() => this.setState({ resultRendered: true })}
                message={<span id="message-id">{drawMessage}</span>}
            />
            <Snackbar
                open={this.state.fetchError}
                autoHideDuration={6000}
                onClose={() => this.setState({ fetchError: false })}
                message={<span id="message-id">Error performing operation</span>}
            />
        </div>)
    }

    renderButtons(theme) {
        const needsSearchButton = this.state.method === 'KNOWN_BOUNDARY'

        return (<React.Fragment>
            <div className={theme.formGroup}>
                {this.props.onCancel && (
                    <Button
                        classes={{
                            root: theme.buttonSubmit,
                        }}
                        onClick={() => this.props.onCancel()}
                    >
                        <span>Cancel</span>
                    </Button>
                )}
                {needsSearchButton && this.state.fetchingKnownBoundaries &&
                <span><i className="fa fa-refresh fa-spin" /> Searching...</span>
                }
            </div>
        </React.Fragment>)
    }

    renderKnownBoundaryControls(theme) {
        const boundaryLabel = this.state.fetchingGeometry
            ? 'Searching...'
            : 'Boundary'
        const hasKnownBoundaries = !!(this.state.knownBoundaries && this.state.knownBoundaries.length)
        const sort = (a, b) => { if (a.name < b.name) return -1; else if (a.name > b.name) return 1; return 0 }

        if (this.state.method === 'KNOWN_BOUNDARY') {
            return (<React.Fragment>
                <div className={theme.controlGroup}>
                    <Grid container direction={'row'} alignItems={'center'} justify={'space-between'} wrap={'nowrap'} alignContent={'stretch'}>
                        <Grid item xs={11} >
                            <FormControl fullWidth>
                                <InputLabel htmlFor={'KNOWN_BOUNDARY_TYPE'}>Type of Boundary</InputLabel>
                                <Select
                                    autoWidth
                                    value={this.state.knownBoundaryType}
                                    inputProps={{
                                        name: 'KNOWN_BOUNDARY_TYPE',
                                        id: 'KNOWN_BOUNDARY_TYPE',
                                    }}
                                    onChange={this.setBoundaryType}
                                >
                                    {this.props.knownBoundaryOptions.map(([key, value]) => (
                                        <MenuItem key={key} value={key}>{value}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </div>
                {hasKnownBoundaries && <div>
                    <Grid container direction={'row'} alignItems={'center'} justify={'space-between'} wrap={'nowrap'} alignContent={'stretch'}>
                        <Grid item xs={11} >
                            <FormControl fullWidth>
                                <InputLabel htmlFor={'KNOWN_BOUNDARIES'}>{ boundaryLabel }</InputLabel>
                                <Select
                                    value={this.state.knownBoundaryId}
                                    inputProps={{
                                        name: 'KNOWN_BOUNDARIES',
                                        id: 'KNOWN_BOUNDARIES',
                                    }}
                                    onChange={evt => this.fetchKnownBoundary(evt.target.value)}
                                >
                                    {this.state.knownBoundaries.sort(sort).map(bnd => (
                                        <MenuItem key={bnd.oid} value={bnd.oid}>{bnd.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item><i
                            className={'fa fa-repeat'}
                            onClick={this.onReloadBoundaries}
                            role="button"
                            tabIndex={0}
                        />
                        </Grid>
                    </Grid>
                </div>}
            </React.Fragment>)
        }
        return (null)
    }

    handleBufferRadiusChange(evt) {
        this.vectorSource.clear()
        this.setState({ bufferRadius: evt.target.value })
        this.activateDrawTool(this.state.method)
    }

    renderDrawFeatureControls(theme) {
        return (<div>
            {(this.state.method === 'DRAW_POINT' || this.state.method === 'DRAW_LINE') &&
                <div>
                    <FormControl>
                        <TextField
                            id="buffer"
                            label="buffer"
                            onChange={evt => this.handleBufferRadiusChange(evt)}
                            value={this.state.bufferRadius}
                        />
                    </FormControl>
                    <FormControl>
                        <InputLabel htmlFor={'buffer-units'}>Units</InputLabel>
                        <Select
                            autoWidth
                            inputProps={{
                                name: 'buffer-units',
                                id: 'buffer-units',
                            }}
                            onChange={evt => this.setState({ bufferUnits: evt.target.value })}
                            value={this.state.bufferUnits}
                        >
                            <MenuItem value={'miles'}>Miles</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            }
        </div>)
    }

    renderUpload() {
        const onDrop = (acceptedFiles) => {
            // Get the features and the envelope
            const req = request.post('/er2_map/api/v1/get_geojson/')
            acceptedFiles.forEach(file => req.attach(file.name, file))
            req.then((resp) => {
                const json = JSON.parse(resp.text)
                // Returns both the envelope layer containing a single feature and layer
                const feature = json.envelope.features[0]
                const olFeat = olUtils.readFeatures(feature)
                this.onDrawFeatures(olFeat, json.geojson)
                const extent = this.vectorSource.getExtent()
                this.props.er2Map.olmap.getView().fit(extent, this.props.er2Map.olmap.getSize())
            })
        }
        return (<div style={{ cursor: 'pointer', textAlign: 'center' }}>
            <Dropzone
                onDrop={onDrop}
                style={{ display: 'inline-block', margin: '6px' }}
            >
                <Button>Import</Button>
            </Dropzone>
        </div>)
    }

    renderTool(theme) {
        const titleCase = s => `${s[0].toUpperCase()}${s.substr(1).toLowerCase()}`
        return (<React.Fragment>
            <div className={theme.controlGroup} style={{ position: 'relative' }}>
                <Grid container direction={'row'} alignItems={'center'} justify={'space-between'} wrap={'nowrap'} alignContent={'stretch'}>
                    <Grid item xs={11} >
                        <FormControl fullWidth>
                            <InputLabel htmlFor={'digitize'}>Method</InputLabel>
                            <Select
                                value={this.state.method}
                                inputProps={{
                                    name: 'digitize',
                                    id: 'digitize',
                                }}
                                onChange={this.setMethod}
                            >
                                {this.props.shapes.map(s => (<MenuItem key={`DRAW_${s}`} value={`DRAW_${s}`}>Draw a {titleCase(s)}</MenuItem>))}
                                {this.props.includeKnownBoundaries && (
                                    <MenuItem key={'KNOWN_BOUNDARY'} value={'KNOWN_BOUNDARY'}>Known Boundaries</MenuItem>
                                )}
                                <MenuItem value={'UPLOAD'}>User-Supplied Layer</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {this.state.method !== 'KNOWN_BOUNDARY' && this.state.method && (
                        <Grid item>
                            <i
                                className={`fa ${this.state.isDrawing ? 'fa-pencil-square' : 'fa-pencil'}`}
                                onClick={() => this.activateDrawTool(this.state.method)}
                                role="button"
                                tabIndex={0}
                            />
                        </Grid>
                    )}
                    {this.state.method === 'UPLOAD' && this.renderUpload()}
                </Grid>
            </div>
            {this.props.includeKnownBoundaries && this.renderKnownBoundaryControls(theme)}
            {this.state.method !== 'KNOWN_BOUNDARY' && this.renderDrawFeatureControls(theme)}
            {this.props.extraFilters.map(f => (
                <TextField
                    id={f.filter}
                    key={f.filter}
                    label={f.filter}
                    type="search"
                    margin="normal"
                    onChange={e => this.updateFilter(f.filter, e.target.value)}
                    value={this.state[f.filter]}
                />

            ))}
            {this.renderButtons(theme)}
        </React.Fragment>)
    }

    render() {
        const theme = this.props.theme

        return (<div className={theme.geoBarCtr}>
            <div className={theme.formGroup}>
                <span className={theme.toolLabel}>{this.props.toolLabel}</span>
            </div>
            <div>
                {this.renderTool(theme)}
            </div>
            {this.renderNotifications(theme)}
        </div>)
    }
}

export default AOISelect
